import { useLocation, useOutlet } from "react-router";
import { Suspense, useState, useEffect, useRef } from "react";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { CommandPalette } from "@/components/shared/CommandPalette";
import { KeyboardShortcutsDialog } from "@/components/shared/KeyboardShortcutsDialog";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { useThemeMode } from "@/context/ThemeContext";
import { TopProgressBar } from "@/components/ui/TopProgressBar";
import { useGlobalRealtime } from "@/hooks/useGlobalRealtime";

import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { ChevronUp } from "lucide-react";

/** Reactive media-query hook for breakpoint awareness */
function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia(query).matches : false,
    );
    useEffect(() => {
        const mql = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
        mql.addEventListener("change", handler);
        setMatches(mql.matches);
        return () => mql.removeEventListener("change", handler);
    }, [query]);
    return matches;
}

export function AppShell() {
    const location = useLocation();
    const outlet = useOutlet();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        if (typeof window === "undefined") return false;
        return localStorage.getItem("mediflow-sidebar-collapsed") === "true";
    });
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const mainRef = useRef<HTMLElement>(null);
    const { toggleTheme } = useThemeMode();
    const isDesktop = useMediaQuery("(min-width: 1024px)");

    // Initialize Global Realtime sync for React Query Cache
    useGlobalRealtime();

    // Persist sidebar state
    useEffect(() => {
        localStorage.setItem("mediflow-sidebar-collapsed", String(sidebarCollapsed));
    }, [sidebarCollapsed]);

    // Global keyboard shortcuts: ⌘K, ⌘⇧K, ⌘D
    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setShortcutsOpen((prev) => !prev);
            } else if ((e.metaKey || e.ctrlKey) && e.key === "k") {
                e.preventDefault();
                setCommandPaletteOpen((prev) => !prev);
            } else if ((e.metaKey || e.ctrlKey) && e.key === "d") {
                e.preventDefault();
                toggleTheme();
            }
        }
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleTheme]);

    return (
        <MotionConfig reducedMotion="user">
            <div className="flex h-screen transition-colors duration-300" style={{ backgroundColor: 'var(--surface-canvas)' }}>
                <TopProgressBar />

                {/* Mobile sidebar backdrop */}
                <AnimatePresence>
                    {mobileSidebarOpen && (
                        <motion.div
                            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:hidden"
                            onClick={() => setMobileSidebarOpen(false)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        />
                    )}
                </AnimatePresence>

                <Sidebar
                    collapsed={sidebarCollapsed}
                    onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                    mobileOpen={mobileSidebarOpen}
                    onMobileClose={() => setMobileSidebarOpen(false)}
                    isDesktop={isDesktop}
                />

                {/*
                  * Shell motion fix: Replace framer-motion marginLeft animation with CSS transition.
                  * framer-motion JS animation = rAF loop updating style.marginLeft every ~16ms → layout recalc every frame.
                  * CSS transition = browser schedules ONE layout pass, then interpolates — far lower CPU/GPU cost.
                  * The sidebar is position:fixed, so its width change causes ZERO layout impact here.
                  */}
                <div
                    className="flex flex-1 flex-col min-w-0 overflow-x-hidden"
                    style={{
                        marginLeft: isDesktop ? (sidebarCollapsed ? 68 : 260) : 0,
                        transition: "margin-left 300ms cubic-bezier(0.32, 0.72, 0, 1)",
                        willChange: "margin-left",
                    }}
                >
                    <Topbar
                        onSearchClick={() => setCommandPaletteOpen(true)}
                        onMenuClick={() => setMobileSidebarOpen(true)}
                    />

                    <main
                        ref={mainRef}
                        className="flex-1 min-h-0 overflow-auto"
                        onScroll={(e) => setShowScrollTop(e.currentTarget.scrollTop > 400)}
                    >
                        <ErrorBoundary>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={location.pathname}
                                    className="flex h-full min-h-full min-w-0 flex-col"
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -4 }}
                                    transition={{ duration: 0.15, ease: "easeOut" }}
                                >
                                    <Suspense
                                        fallback={
                                            <div className="flex min-h-full flex-1 flex-col" style={{ backgroundColor: 'var(--surface-canvas)' }}>
                                                <div className="h-16 shrink-0 border-b border-neutral-100 dark:border-neutral-700/80 glass px-6" />
                                                <div className="flex flex-1 items-center justify-center">
                                                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 dark:border-neutral-700 border-t-brand-500 dark:border-t-brand-400" />
                                                </div>
                                            </div>
                                        }
                                    >
                                        {outlet}
                                    </Suspense>
                                </motion.div>
                            </AnimatePresence>
                        </ErrorBoundary>
                    </main>
                </div>

                {/* ScrollToTop */}
                <AnimatePresence>
                    {showScrollTop && (
                        <motion.button
                            className="fixed bottom-6 right-6 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-white shadow-elevated hover:bg-brand-600 transition-colors"
                            onClick={() =>
                                mainRef.current?.scrollTo({ top: 0, behavior: "smooth" })
                            }
                            initial={{ opacity: 0, y: 12, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 12, scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                            aria-label="Nach oben scrollen"
                            style={{ minHeight: 44, minWidth: 44 }}
                        >
                            <ChevronUp className="h-5 w-5" />
                        </motion.button>
                    )}
                </AnimatePresence>

                <CommandPalette
                    open={commandPaletteOpen}
                    onClose={() => setCommandPaletteOpen(false)}
                />
                <KeyboardShortcutsDialog
                    open={shortcutsOpen}
                    onClose={() => setShortcutsOpen(false)}
                />
            </div>
        </MotionConfig>
    );
}
